import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, UserRound } from "lucide-react";

/** Edit Requests Phase 1 (2026-09) — assigns a PM to a client_project_requests row BEFORE any
 * project exists. Deliberately its own small field on the request doc (never
 * `client_project_pm`, which needs a project's Notion remote_id) — same UI shape/style as
 * ClientPortalPmCard.jsx, different backend target (`PUT /client-project-requests/{id}/pm`). */
export const RequestPmAssign = ({ requestId, initialPm }) => {
  const [pm, setPm] = useState(initialPm || null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(initialPm || { name: "", role_title: "Project Manager", email: "", whatsapp: "", photo_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPm(initialPm || null); if (initialPm) setForm(initialPm); }, [initialPm]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/v1/client-project-requests/${requestId}/pm`, form);
      setPm(r.data.request.assigned_pm);
      setEditing(false);
      toast.success("PM assigned to this request");
    } catch {
      toast.error("Couldn't assign a PM");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-[#E7E5E4] bg-[#FAFAF9]" data-testid={`request-pm-assign-${requestId}`}>
      <header className="px-3 py-2 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-[#57534E] inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> Assigned PM</span>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid={`request-pm-edit-btn-${requestId}`}
            className="text-[11px] uppercase tracking-wider text-[#57534E] hover:text-[#B45309]">{pm ? "Edit" : "Assign"}</button>
        )}
      </header>
      {!editing ? (
        <div className="px-3 pb-2 text-xs">
          {pm ? (
            <span data-testid={`request-pm-summary-${requestId}`}>{pm.name} <span className="text-[#A8A29E]">· {pm.role_title}</span></span>
          ) : (
            <span className="text-[#78716C]" data-testid={`request-pm-empty-${requestId}`}>Not yet assigned</span>
          )}
        </div>
      ) : (
        <div className="px-3 pb-3 space-y-1.5">
          <input data-testid={`request-pm-name-input-${requestId}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name" className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <input data-testid={`request-pm-email-input-${requestId}`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email" className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <div className="flex items-center gap-2 pt-1">
            <button onClick={save} disabled={saving} data-testid={`request-pm-save-btn-${requestId}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-2.5 py-1 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
            </button>
            <button onClick={() => { setEditing(false); if (pm) setForm(pm); }} data-testid={`request-pm-cancel-btn-${requestId}`} className="text-xs text-[#A8A29E] hover:text-[#1C1C1E]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
